<template>
  <div ref="scroll" class="mugen-scroll">
    <slot></slot>
  </div>
</template>

<script>
/**
 * Vue 3 compatible replacement for vue-mugen-scroll.
 * Uses IntersectionObserver to detect when the element is visible
 * and triggers the handler for infinite scrolling.
 *
 * Props match the original vue-mugen-scroll API:
 *   - handler: Function to call when scrolled into view
 *   - shouldHandle: Boolean to enable/disable the handler
 */
export default {
  name: "MugenScroll",
  props: {
    handler: {
      type: Function,
      required: true,
    },
    shouldHandle: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      observer: null,
    };
  },
  mounted() {
    this.setupObserver();
  },
  beforeUnmount() {
    this.destroyObserver();
  },
  watch: {
    shouldHandle(val) {
      if (val) {
        this.checkVisibility();
      }
    },
  },
  methods: {
    setupObserver() {
      if (typeof IntersectionObserver === "undefined") return;

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && this.shouldHandle) {
            this.handler();
          }
        },
        {
          rootMargin: "0px",
          threshold: 0,
        }
      );

      if (this.$refs.scroll) {
        this.observer.observe(this.$refs.scroll);
      }
    },
    destroyObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    },
    checkVisibility() {
      // Re-check in case the element is already in view when shouldHandle becomes true
      if (!this.$refs.scroll || !this.shouldHandle) return;
      const rect = this.$refs.scroll.getBoundingClientRect();
      const inView =
        rect.top <
        (window.innerHeight || document.documentElement.clientHeight);
      if (inView) {
        this.handler();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// A 0px-tall sentinel can sit just past a fractional scroll edge and never intersect.
.mugen-scroll {
  min-height: 1px;
}
</style>
